import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XIcon,
} from "@heroicons/react/solid";
import FighterScoreCard from "../home/FighterScoreCard";
import { useUserById } from "../../hooks/user/useUserById";
import VerificationBadge from "../user/VerificationBadge";

/**
 * Reusable component for displaying a submission with expandable details
 * Used in both ActiveSubmissionsCard and ScorecardPage
 */
const SubmissionDisplay = ({
  submission,
  showLeagueName = false,
  showEventName = false,
  showUserInfo = false,
  showViewEventButton = false,
  isExpanded: controlledExpanded,
  onToggle: controlledToggle,
  className = "",
  previewOnly = false, // New prop: when true, only shows basic info (for events that haven't started)
}) => {
  const navigate = useNavigate();
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Use controlled or internal state
  const isExpanded =
    controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const toggleExpanded =
    controlledToggle || (() => setInternalExpanded(!internalExpanded));

  // Handle userId - it can be either a string (ID) or an object (populated user data)
  const userIdString =
    typeof submission.userId === "object" && submission.userId !== null
      ? submission.userId._id
      : submission.userId;

  // If userId is already populated with user data, use it directly
  const userDataFromSubmission =
    typeof submission.userId === "object" && submission.userId !== null
      ? submission.userId
      : null;

  // Fetch user data only if not already populated and we need to show user info
  const { data: fetchedUserData } = useUserById(
    showUserInfo && !userDataFromSubmission ? userIdString : null,
  );

  // Use populated data if available, otherwise use fetched data
  const userData = userDataFromSubmission || fetchedUserData;

  const totalScore = submission.total || 0;

  // Check if event has started
  const eventDateTime = submission.eventDateTime || submission.DateTime;
  const hasEventStarted = eventDateTime
    ? new Date(eventDateTime) <= new Date()
    : false;

  const handleViewEvent = () => {
    if (showViewEventButton && submission.eventId && submission.leagueId) {
      navigate(`/events/${submission.eventId}`, {
        state: { leagueId: submission.leagueId },
      });
    }
  };

  // Preview mode: Simple card with just user info and projected score
  if (previewOnly) {
    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {showUserInfo && userData && (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/users/${userIdString}`)}
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
                  aria-label={`View ${userData.username || "user"}'s profile`}
                >
                  {userData.picture ? (
                    <img
                      src={userData.picture}
                      alt={userData.username || "User"}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-deep-red flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {userData.username?.[0]?.toUpperCase() || "?"}
                      </span>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-dark-grey hover:underline">
                        {userData.username || "Unknown User"}
                      </span>
                      {userData.verified && (
                        <VerificationBadge
                          verified={userData.verified}
                          size="small"
                          showTooltip={false}
                        />
                      )}
                    </div>
                    <div className="text-xs text-medium-grey text-left">
                      Projected: {(submission.projectedTotal || 0).toFixed(2)}{" "}
                      pts
                    </div>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors ${className}`}
    >
      {/* Header */}
      <div
        className="bg-gradient-to-r from-gray-700 to-gray-600 p-4 cursor-pointer"
        onClick={toggleExpanded}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {/* User Info */}
            {showUserInfo && userData && (
              <div className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/users/${userIdString}`);
                  }}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                  aria-label={`View ${userData.username || "user"}'s profile`}
                >
                  {userData.picture ? (
                    <img
                      src={userData.picture}
                      alt={userData.username || "User"}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {userData.username?.[0]?.toUpperCase() || "?"}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-base hover:underline">
                      {userData.username || "Unknown User"}
                    </span>
                    {userData.verified && (
                      <VerificationBadge
                        verified={userData.verified}
                        size="small"
                        showTooltip={false}
                      />
                    )}
                  </div>
                </button>
              </div>
            )}

            {/* League Name */}
            {showLeagueName && submission.leagueName && (
              <h3 className="text-white font-bold text-lg mb-1 truncate">
                {submission.leagueName}
              </h3>
            )}

            {/* Event Name */}
            {showEventName && submission.eventName && (
              <p className="text-white/90 text-sm truncate">
                {submission.eventName}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 ml-4">
            {totalScore !== 0 || hasEventStarted ? (
              <div
                className={`px-4 py-2 rounded-lg font-bold text-lg ${
                  totalScore >= 0
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {totalScore >= 0 ? "+" : ""}
                {totalScore.toFixed(2)}
              </div>
            ) : (
              <div className="px-3 py-2 rounded-lg font-bold bg-blue-500 text-white">
                <div className="text-[10px] opacity-80">Proj.</div>
                <div className="text-base">
                  {(submission.projectedTotal || 0).toFixed(2)}
                </div>
              </div>
            )}
            <ChevronDownIcon
              className={`w-5 h-5 text-white transform transition-transform duration-200 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-4 bg-gray-50">
          {/* Lineup */}
          <div className="space-y-2 mb-4">
            <h4 className="font-bold text-dark-grey text-sm mb-2">
              Lineup ({submission.selections?.length || 0} fighters)
            </h4>
            {submission.selections?.map((selection, idx) => (
              <FighterScoreCard
                key={selection._id || idx}
                selection={{ ...selection, hasEventStarted }}
                idx={idx}
              />
            ))}
          </div>

          {/* Alternates */}
          {submission.alternates && submission.alternates.length > 0 && (
            <div className="space-y-2 mb-4">
              <h4 className="font-bold text-dark-grey text-sm mb-2">
                Alternates ({submission.alternates.length})
              </h4>
              {submission.alternates.map((alt, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg p-3 border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-medium-grey text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                      A{idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-dark-grey">
                        {alt.fighter}
                      </div>
                      {alt.lotn && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gold text-dark-grey mt-1">
                          LOTN
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cancelled Fighters */}
          {submission.adjustments?.cancellations &&
            submission.adjustments.cancellations.length > 0 && (
              <div className="space-y-2 mb-4">
                <h4 className="font-bold text-dark-grey text-sm mb-2">
                  Cancelled Fighters (
                  {submission.adjustments.cancellations.length})
                </h4>
                {submission.adjustments.cancellations.map(
                  (cancellation, idx) => (
                    <div
                      key={cancellation.fighterId || idx}
                      className="bg-white rounded-lg p-3 border border-red-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                          <XIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-dark-grey">
                              {cancellation.fighterName}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">
                              CANCELLED
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

          {/* View Event Button */}
          {showViewEventButton && (
            <button
              onClick={handleViewEvent}
              className="w-full bg-deep-red hover:bg-bright-red text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              View Event Details
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubmissionDisplay;
